import React from 'react';
import CodeBlock from '../../CodeBlock';
import { RefreshCw, CheckCircle, Eye, FileCheck } from 'lucide-react';

const SOPRefinementWorkflow = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <div className="workflow-section">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-white">SOP Refinement</h1>
        <p className="text-lg md:text-xl text-gray-300 leading-7 max-w-[65ch]">
          Iteratively refine your SOPs through a structured review process. Answer clarification questions,
          review proposed changes, and apply improvements to perfect your operating procedures.
        </p>
      </div>

      <div className="card mb-6 bg-gray-900 border border-gray-800">
        <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">Overview</h3>
        <p className="text-sm text-gray-300 leading-6 mb-4">
          The SOP refinement workflow enables collaborative improvement of your SOPs through clarifications and proposals.
          When an SOP is generated, the system identifies areas that may need clarification. Answering these questions
          triggers the creation of change proposals, which you can review and approve to automatically update your SOP.
        </p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Get SOP */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">1</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Retrieve your SOP</h3>
              <p className="text-sm text-gray-400 leading-6">Get the SOP you want to refine. Ensure it's in a reviewable state.</p>
            </div>
          </div>
          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/sops/{sop_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "{sop_uuid}",
  "project_uuid": "{project_uuid}",
  "title": "Customer Support Escalation SOP",
  "status": "PROPOSED",
  "version": 1,
  "scope": {...},
  "content": {...},
  "steps": [...]
}`} />
            </div>
          </div>
        </div>

        {/* Step 2: Get Clarifications */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">2</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Get clarification questions</h3>
              <p className="text-sm text-gray-400 leading-6">Retrieve the list of clarifications that need answers to improve your SOP.</p>
            </div>
          </div>
          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/sops/{sop_uuid}/clarifications \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`[
  {
    "uuid": "{clarification_uuid}",
    "sop_uuid": "{sop_uuid}",
    "step_uuid": "{step_uuid}",
    "clarification_type": "GAP",
    "question_text": "What should happen if the customer is still unsatisfied after escalation?",
    "answer_text": null,
    "status": "PENDING",
    "clarification_metadata": null
  }
]`} />
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-200 flex items-start gap-2">
                <Eye className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Note:</strong> Clarifications are generated automatically when an SOP is created.
                  They identify areas where additional context might improve the procedure's clarity or completeness.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 3: Answer Clarification */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">3</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Answer a clarification</h3>
              <p className="text-sm text-gray-400 leading-6">Provide answers to clarification questions. This triggers automatic proposal generation.</p>
            </div>
          </div>
          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/clarifications/{clarification_uuid}/answer \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "answer_text": "If the customer is still unsatisfied after escalation, the case should be forwarded to senior management with a detailed summary of all interactions and escalation attempts."
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (202 Accepted)</h4>
              <CodeBlock code={`{
  "clarification": {
    "uuid": "{clarification_uuid}",
    "sop_uuid": "{sop_uuid}",
    "step_uuid": "{step_uuid}",
    "clarification_type": "GAP",
    "question_text": "What should happen if the customer is still unsatisfied after escalation?",
    "answer_text": "If the customer is still unsatisfied after escalation, the case should be forwarded to senior management...",
    "status": "ANSWERED",
    "clarification_metadata": null
  },
  "message": "Answer recorded. Proposal generation in progress. Check proposals endpoint for status."
}`} />
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-200 flex items-start gap-2">
                <RefreshCw className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Proposal generation:</strong> Answering a clarification automatically
                  triggers proposal generation in the background. Wait a few seconds, then check the proposals endpoint
                  to see the generated change proposal.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 4: Get Proposals */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">4</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Review change proposals</h3>
              <p className="text-sm text-gray-400 leading-6">Get all proposals for your SOP and review the suggested changes.</p>
            </div>
          </div>
          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request (All proposals)</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/sops/{sop_uuid}/proposals \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request (Filter by status)</h4>
              <CodeBlock code={`curl -X GET "${BASE_URL}/sops/{sop_uuid}/proposals?status=PENDING" \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`[
  {
    "uuid": "{proposal_uuid}",
    "clarification_uuid": "{clarification_uuid}",
    "sop_uuid": "{sop_uuid}",
    "step_uuid": "{step_uuid}",
    "status": "PENDING",
    "before_snapshot": {...},
    "proposed_changes": {
      "step_updates": [
        {
          "step_uuid": "{step_uuid}",
          "updates": {
            "action_text": "Updated action text based on clarification",
            "step_metadata": {...}
          },
          "diffs": {...}
        }
      ]
    },
    "justification": "Based on the clarification answer, we should update this step...",
    "analysis": {...},
    "created_by_user_uuid": "{user_uuid}",
    "reviewed_by_user_uuid": null,
    "proposal_metadata": null,
    "created_at": "2024-01-15T10:05:30Z",
    "updated_at": "2024-01-15T10:05:30Z"
  }
]`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-3">Get proposal for a specific clarification</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/clarifications/{clarification_uuid}/proposal \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>
          </div>
        </div>

        {/* Step 5: Review Proposal */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">5</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Approve or reject proposals</h3>
              <p className="text-sm text-gray-400 leading-6">Review proposals and decide whether to approve or reject the suggested changes.</p>
            </div>
          </div>
          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request (Approve)</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/proposals/{proposal_uuid}/review \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "action": "approve",
    "review_notes": "These changes look good and align with our escalation policy."
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request (Reject)</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/proposals/{proposal_uuid}/review \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "action": "reject",
    "review_notes": "This doesn't match our current policy."
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (Approved)</h4>
              <CodeBlock code={`{
  "uuid": "{proposal_uuid}",
  "clarification_uuid": "{clarification_uuid}",
  "sop_uuid": "{sop_uuid}",
  "step_uuid": "{step_uuid}",
  "status": "APPROVED",
  "before_snapshot": {...},
  "proposed_changes": {...},
  "justification": "...",
  "analysis": {...},
  "created_by_user_uuid": "{user_uuid}",
  "reviewed_by_user_uuid": "{reviewer_uuid}",
  "proposal_metadata": null,
  "created_at": "2024-01-15T10:05:30Z",
  "updated_at": "2024-01-15T10:10:00Z"
}`} />
            </div>

            <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
              <p className="text-sm text-green-200 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Automatic application:</strong> When you approve a proposal,
                  the changes are automatically applied to your SOP in the background. You can verify the changes
                  by retrieving the SOP again.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 6: Direct Step Updates (Optional) */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">6</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Direct step updates (optional)</h3>
              <p className="text-sm text-gray-400 leading-6">Manually update steps or reorder them without going through the proposal process.</p>
            </div>
          </div>
          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Update a step</h4>
              <CodeBlock code={`curl -X PATCH ${BASE_URL}/sops/{sop_uuid}/steps/{step_id} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "action_text": "Updated action text",
    "step_metadata": {
      "title": "Updated Step Title",
      "description": "Updated description",
      "business_logic": ["Updated logic"]
    }
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Reorder steps</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/sops/{sop_uuid}/steps/{step_id}:move \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "new_index": 2
  }'`} />
            </div>
          </div>
        </div>

        {/* Step 7: Publish SOP */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">7</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Publish your SOP</h3>
              <p className="text-sm text-gray-400 leading-6">Once your SOP is finalized, publish it to mark it as the official version.</p>
            </div>
          </div>
          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/sops/{sop_uuid}:publish \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "{sop_uuid}",
  "project_uuid": "{project_uuid}",
  "title": "Customer Support Escalation SOP",
  "status": "PUBLISHED",
  "version": 1,
  "scope": {...},
  "content": {...},
  "steps": [...]
}`} />
            </div>

            <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
              <p className="text-sm text-green-200 flex items-start gap-2">
                <FileCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Finalization:</strong> Publishing an SOP marks it as the official version.
                  Published SOPs are ready for use in production environments.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Best Practices */}
      <div className="card mt-8 bg-gray-900 border border-gray-800">
        <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">Best Practices for SOP Refinement</h3>
        <ul className="space-y-2 text-sm text-gray-300 list-disc list-inside ml-2">
          <li>Review all clarifications before starting to answer them. This gives you a complete picture of potential improvements.</li>
          <li>Answer clarifications thoroughly and clearly. Detailed answers lead to better proposals.</li>
          <li>Review proposals carefully before approving. The diffs show exactly what will change.</li>
          <li>Iterate through multiple rounds of clarifications and proposals to refine your SOP incrementally.</li>
          <li>Use direct step updates for quick fixes, but prefer the proposal workflow for substantial changes.</li>
          <li>Only publish your SOP once you're satisfied with all changes and it's ready for production use.</li>
        </ul>
      </div>
    </div>
  );
};

export default SOPRefinementWorkflow;
